/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';
import {
  loadSavedPreferences,
  mapGradesByLearner,
  remapProgressionsByLearner,
  remapSummariesByLearner,
} from 'linced-app/utils/data-cards';

export default class DataWallController extends Controller {
  @service
  features;

  @service
  options;

  @service
  personOptions;

  @service
  router;

  @service
  session;

  @service
  strings;

  @tracked
  orientation = 'landscape';

  @tracked
  editDialog = false;

  showAttendance = true;

  showAssessments = true;

  showTopics = true;

  showGoals = true;

  @tracked
  optionsSaveInProgress = false;

  @tracked
  selectedAssessments = [];

  @tracked
  selectedModules = [];

  @tracked
  selectedProgressSubjects = [];

  @tracked
  selectedGoalSubjects = [];

  @setDiff('model.assessments', 'selectedAssessments')
  remainingAssessments;

  @computed
  get userOptions() {
    return this.personOptions.getAllDataCardOptions(this.session.person, [
      'assessmentIds',
      'assessmentModuleIds',
      'subjectIds',
      'progressIds',
    ]);
  }

  @computed('goalSubjects.@each.id', 'userOptions.[]')
  get isSelectedByDefault() {
    let userOptions = this.personOptions.getAllDataCardOptions(
      this.session.person,
      ['assessmentIds', 'assessmentModuleIds', 'subjectIds', 'progressIds'],
    );
    const userSubjectIds = userOptions.subjectIds || [];
    return this.goalSubjects.reduce((acc, goalSubject) => {
      acc[goalSubject.id] = userSubjectIds.includes(goalSubject.id);
      return acc;
    }, {});
  }

  // Initialize component when model loads
  init() {
    super.init(...arguments);
    this.selectedAssessments = [];
    this.selectedModules = [];
    this.selectedProgressSubjects = [];
    this.selectedGoalSubjects = [];
  }

  // Load all saved options from personOptions service

  @computed('model.allSubjects.@each.trackable')
  get progressSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];

    return allSubjects
      .filter(
        (subject) =>
          subject.trackable &&
          subject.visibleToTeachers &&
          !isEmpty(subject.id),
      )
      .sort(byKeys('sequence', 'label'));
  }

  @computed('model.allSubjects.@each.{isTopic,visibleInMenu}')
  get goalSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];
    return allSubjects
      .filter(
        (subject) =>
          !subject.isTopic && subject.visibleToTeachers && !isEmpty(subject.id),
      )
      .sort(byKeys('sequence', 'label'));
  }

  @computed('model.allSubjects.@each.{isTopic,visibleInMenu,subjectTypeName}')
  get groupedGoalSubjects() {
    const allSubjects = this.get('model.allSubjects');
    if (!allSubjects) return [];

    const filteredSubjects = allSubjects
      .filter(
        (subject) =>
          !subject.isTopic &&
          subject.visibleToTeachers &&
          !isEmpty(subject.id) &&
          subject.get('subjectTypeName') !== 'Progress', // Exclude 'Progress' type
      )
      .sort(byKeys('sequence', 'label'));

    // Group subjects by subjectTypeName
    const groupedSubjects = [];
    const subjectTypeMap = {};

    filteredSubjects.forEach((subject) => {
      const typeName = subject.get('subjectTypeName') || 'Other';

      if (!subjectTypeMap[typeName]) {
        const group = {
          typeName: typeName,
          subjects: [],
        };
        groupedSubjects.push(group);
        subjectTypeMap[typeName] = group;
      }

      subjectTypeMap[typeName].subjects.push(subject);
    });

    return groupedSubjects;
  }

  @computed('model.subjectProgressions.@each.{personId,subjectId}')
  get learnerProgressions() {
    return remapProgressionsByLearner(this.model.subjectProgressions);
  }

  @computed('model.subjectSummaries.@each.{personId,subjectId}')
  get learnerSummaries() {
    return remapSummariesByLearner(this.model.subjectSummaries);
  }

  @computed('model.grades.[]')
  get learnerGrades() {
    return mapGradesByLearner(this.model.grades);
  }

  @action
  openDialog() {
    // Reset/reload user preferences.
    loadSavedPreferences(this, this.model, [
      'assessmentIds',
      'assessmentModuleIds',
      'subjectIds',
      'progressIds',
    ]);
    this.editDialog = true;
  }

  @action
  closeDialog() {
    this.editDialog = false;
  }

  @action
  setOrientation(orientation) {
    this.orientation = orientation;
  }

  @action
  toggleSelectedModule(module) {
    if (this.selectedModules.includes(module)) {
      this.selectedModules = this.selectedModules.filter((m) => m !== module);
    } else {
      this.selectedModules = [...this.selectedModules, module];
    }
  }

  @action
  toggleSelectedProgressSubjects(subject) {
    if (this.selectedProgressSubjects.includes(subject)) {
      this.selectedProgressSubjects = this.selectedProgressSubjects.filter(
        (s) => s !== subject,
      );
    } else {
      this.selectedProgressSubjects = [
        ...this.selectedProgressSubjects,
        subject,
      ];
    }
  }

  @action
  toggleSelectedGoalSubjects(subject) {
    if (this.selectedGoalSubjects.includes(subject)) {
      this.selectedGoalSubjects = this.selectedGoalSubjects.filter(
        (s) => s !== subject,
      );
    } else {
      this.selectedGoalSubjects = [...this.selectedGoalSubjects, subject];
    }
  }

  @action
  saveSelections() {
    this.optionsSaveInProgress = true;
    let assessmentIds = this.selectedAssessments.map(
      (assessment) => assessment.id,
    );
    let moduleIds = this.selectedModules.map((module) => module.id);
    let progressSubjectIds = this.selectedProgressSubjects.map(
      (subject) => subject.id,
    );
    let goalSubjectIds = this.selectedGoalSubjects.map((subject) => subject.id);

    this.personOptions
      .updateAllDataCardOptions(this.session.person, {
        assessmentIds: assessmentIds,
        assessmentModuleIds: moduleIds,
        subjectIds: goalSubjectIds,
        progressIds: progressSubjectIds,
      })
      .then(() => {
        this.editDialog = false;
        this.router.refresh('group.data-wall');
      })
      .catch(() => {
        this.toaster.error('Error saving selections. Please try again.');
      })
      .finally(() => {
        this.optionsSaveInProgress = false;
      });
  }

  @action
  addAssessment(assessment) {
    this.selectedAssessments = [...this.selectedAssessments, assessment];
  }

  @action
  removeAssessment(assessment) {
    this.selectedAssessments = this.selectedAssessments.filter(
      (a) => a !== assessment,
    );
  }

  @action
  getBadgeClass(personId) {
    if (this.sortType === 'progress') {
      let cache = findBy(this.progressCache, 'subjectId', this.subjectId);
      if (isEmpty(cache)) {
        return '';
      }
      let data = findBy(cache.data, 'personId', personId);
      if (!isEmpty(data)) {
        return data.badgeClass;
      }
    }

    return '';
  }
}
